import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Quote, Plus, X, ChevronDown } from "lucide-react";
import { TIERS } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE = 24;

const tierMeta = (key) => TIERS.find((t) => t.key === key) || TIERS[0];

const CelebrationCard = ({ c }) => {
  const t = tierMeta(c.tier);
  return (
    <div
      data-testid={`celebration-card-${c.id}`}
      className="relative rounded-3xl border border-black/5 p-6 overflow-hidden hover:-translate-y-0.5 transition"
      style={{ background: t.bgTint }}
    >
      <div className="tier-stripe" style={{ backgroundColor: t.color }} />
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: `${t.color}18`, color: t.color, border: `1px solid ${t.color}33` }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color }} />
          {t.name.split(" ")[0]}
        </span>
        <span className="text-[0.65rem] text-[#6B7280] uppercase tracking-wider">
          {new Date(c.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </span>
      </div>
      <div className="mt-3 font-heading text-lg font-semibold text-[#1F2937] leading-snug">{c.name}</div>
      {c.dedicated_to && (
        <div className="text-xs text-[#6B7280] mt-0.5">In honor of <span className="text-[#1F2937] font-medium">{c.dedicated_to}</span></div>
      )}
      {c.message && (
        <div className="mt-3 text-sm text-[#4B5563] leading-relaxed flex items-start gap-2">
          <Quote className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: t.color }} strokeWidth={1.8} />
          <span>{c.message}</span>
        </div>
      )}
    </div>
  );
};

const AddPieceDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", tier: "blue", message: "", dedicated_to: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Please add your name (or 'Anonymous').");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/celebration`, form);
      toast.success("Your piece has been added. Thank you!");
      onCreated && onCreated(data);
      onClose && onClose();
      setForm({ name: "", tier: "blue", message: "", dedicated_to: "" });
    } catch {
      toast.error("Could not add your piece. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-piece-dialog">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-3xl bg-white p-6 md:p-8 border border-black/5 shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Add a piece</div>
            <h3 className="mt-1 font-heading text-2xl font-semibold text-[#1F2937]">Leave your mark on the wall.</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-black/5" aria-label="Close">
            <X className="w-5 h-5 text-[#6B7280]" />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Your name *</span>
            <input required data-testid="celebration-input-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                   className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                   placeholder="e.g. The Franklin Family, or Anonymous" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Piece color</span>
            <select data-testid="celebration-input-tier" value={form.tier} onChange={(e) => set("tier", e.target.value)}
                    className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]">
              {TIERS.map((t) => (<option key={t.key} value={t.key}>{`${t.name.split(" ")[0]} Puzzle Piece`}</option>))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Dedicated to (optional)</span>
            <input data-testid="celebration-input-dedication" value={form.dedicated_to} onChange={(e) => set("dedicated_to", e.target.value)}
                   className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                   placeholder="e.g. My son Marcus" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[0.7rem] uppercase tracking-wider font-semibold text-[#6B7280]">Message (optional)</span>
            <textarea rows={3} data-testid="celebration-input-message" value={form.message} onChange={(e) => set("message", e.target.value)}
                      className="w-full rounded-2xl bg-[#F4F1EA] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                      placeholder="Something kind for our community…" />
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#1F2937] hover:bg-black/5">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="celebration-submit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-6 py-3 text-sm font-semibold disabled:opacity-60">
            <Heart className="w-4 h-4" /> Add my piece
          </button>
        </div>
      </form>
    </div>
  );
};

export default function Community() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [tierFilter, setTierFilter] = useState("all");
  const sentinel = useRef(null);
  const inflight = useRef(false);
  const nextSkip = useRef(0);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/celebration/count`);
      setTotal(data.total || 0);
    } catch {}
  }, []);

  const loadMore = useCallback(async () => {
    if (inflight.current || !hasMore) return;
    inflight.current = true;
    setLoading(true);
    try {
      const skip = nextSkip.current;
      const { data } = await axios.get(`${API}/celebration`, { params: { skip, limit: PAGE } });
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const fresh = (data || []).filter((d) => !seen.has(d.id));
        return [...prev, ...fresh];
      });
      nextSkip.current = skip + (data ? data.length : 0);
      if (!data || data.length < PAGE) setHasMore(false);
    } catch {
      toast.error("Could not load more.");
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, [hasMore]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => { loadMore(); /* first page */ /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!sentinel.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) loadMore(); });
    }, { rootMargin: "400px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [loadMore]);

  const displayed = useMemo(
    () => tierFilter === "all" ? items : items.filter((i) => i.tier === tierFilter),
    [items, tierFilter]
  );

  const tierStats = useMemo(() => {
    const stats = { blue: 0, red: 0, yellow: 0, green: 0 };
    items.forEach((i) => { if (stats[i.tier] !== undefined) stats[i.tier]++; });
    return stats;
  }, [items]);

  return (
    <div data-testid="page-community">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Pieces of Hope</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          Our community,<br />
          <span className="brush-underline brush-yellow">piece by piece.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Every dedication, every dollar, every hand raised — this is the wall we build together.
          <span className="text-[#1F2937] font-semibold"> {total.toLocaleString()} pieces</span> and growing.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setDialog(true)}
            data-testid="add-piece-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F2937] hover:bg-black text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition"
          >
            <Plus className="w-4 h-4" /> Add my piece
          </button>
          <div className="flex items-center gap-1 rounded-full bg-white border border-black/5 p-1">
            {["all", "blue", "red", "yellow", "green"].map((k) => {
              const active = tierFilter === k;
              const color = k === "all" ? "#1F2937" : tierMeta(k).color;
              return (
                <button
                  key={k}
                  onClick={() => setTierFilter(k)}
                  data-testid={`filter-${k}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition ${active ? "text-white" : "text-[#6B7280] hover:text-[#1F2937]"}`}
                  style={active ? { backgroundColor: color } : undefined}
                >
                  {k}
                  {k !== "all" && <span className="ml-1.5 opacity-80">{tierStats[k]}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayed.map((c) => <CelebrationCard key={c.id} c={c} />)}
        </div>

        {!displayed.length && !loading && (
          <div className="rounded-3xl bg-[#F4F1EA] border border-black/5 p-10 text-center text-[#6B7280]" data-testid="community-empty">
            No pieces in this color yet. Be the first — add yours!
          </div>
        )}

        <div ref={sentinel} className="h-16 flex items-center justify-center">
          {loading && (
            <div className="inline-flex items-center gap-2 text-sm text-[#6B7280]" data-testid="community-loading">
              <ChevronDown className="w-4 h-4 animate-bounce" /> Loading more pieces…
            </div>
          )}
          {!loading && !hasMore && items.length > 0 && (
            <div className="text-sm text-[#6B7280]" data-testid="community-end">You've reached the top of the wall. Add your piece to keep it growing.</div>
          )}
        </div>
      </section>

      <AddPieceDialog
        open={dialog}
        onClose={() => setDialog(false)}
        onCreated={(c) => {
          setItems((prev) => {
            if (prev.some((p) => p.id === c.id)) return prev;
            return [c, ...prev];
          });
          nextSkip.current += 1;
          setTotal((t) => t + 1);
        }}
      />
    </div>
  );
}
